import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable, EmptyState, Badge, Avatar } from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { userService } from '@/services/user.service';
import type { User } from '@/types/user';

// ─── Design tokens ──────────────────────────────────────────────────────────
const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

const ROLE_VARIANT: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
  OWNER: 'success',
  MANAGER: 'info',
  CAPTAIN: 'warning',
  CASHIER: 'warning',
};

const columns: ColumnDef<User>[] = [
  {
    key: 'name',
    header: 'User',
    render: (v, row) => (
      <div className="flex items-center gap-2">
        <Avatar name={v as string} size="sm" />
        <div>
          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{v as string}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{row.email}</p>
        </div>
      </div>
    ),
  },
  {
    key: 'role',
    header: 'Role',
    render: (v) => (
      <Badge variant={ROLE_VARIANT[v as string] ?? 'info'} size="sm">
        {v as string}
      </Badge>
    ),
  },
  {
    key: 'is_active',
    header: 'Status',
    render: (v) => (
      <Badge variant={(v as boolean) ? 'success' : 'danger'} size="sm">
        {(v as boolean) ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
  {
    key: 'last_login_at',
    header: 'Last Active',
    render: (v) => (
      <span className="tabular-nums text-xs text-neutral-500 dark:text-neutral-400">
        {(v as string)
          ? new Date(v as string).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
          : '—'}
      </span>
    ),
  },
];

interface LocationStaffTabProps {
  locationId: string;
}

export function LocationStaffTab({ locationId }: LocationStaffTabProps) {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['location', locationId, 'staff'],
    queryFn: () => userService.list({ location_id: locationId, limit: 50 }),
  });

  const users = data?.data ?? [];

  if (!isLoading && users.length === 0) {
    return (
      <div className="animate-slide-up">
        <EmptyState
          variant="page"
          title="No staff assigned"
          description="Assign team members to this location from User Management"
          action={{ label: 'Manage Users', onClick: () => navigate('/users') }}
          icon={
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500/20 to-indigo-500/20">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-8 w-8 text-brand-600 dark:text-brand-400" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div className={`${GLASS_PANEL} overflow-hidden animate-slide-up transition-shadow duration-200 hover:shadow-lg`}>
      <DataTable<User>
        columns={columns}
        data={users}
        loading={isLoading}
        variant="default"
        emptyState={{ title: 'No staff', description: 'No staff members assigned to this location' }}
      />
    </div>
  );
}
